/**
 * Configuration des équipements avec icônes et labels pour l'affichage
 * Source unique de vérité pour les équipements - utilisé côté client et serveur
 *
 * Note: Le petit-déjeuner est toujours inclus (garanti chambre d'hôtes).
 * Les animaux sont gérés séparément via HoliPetsToggle.
 */
export const EQUIPMENT_CATEGORIES = {
  stationnement: {
    label: 'Stationnement',
    labelEn: 'Parking',
    icon: 'i-lucide-car',
    items: [
      { key: 'parking_ferme', label: 'Parking fermé', labelEn: 'Enclosed parking', icon: 'i-lucide-lock' },
      { key: 'parking_ouvert', label: 'Parking ouvert', labelEn: 'Open parking', icon: 'i-lucide-square-parking' },
      { key: 'garage_velo_moto', label: 'Garage vélo/moto', labelEn: 'Bike/motorbike garage', icon: 'i-lucide-bike' },
      { key: 'abri_velo_securise', label: 'Abri vélo sécurisé', labelEn: 'Secure bike shelter', icon: 'i-lucide-shield' },
    ],
  },
  accessibilite: {
    label: 'Accessibilité',
    labelEn: 'Accessibility',
    icon: 'i-lucide-accessibility',
    items: [
      { key: 'pmr', label: 'Accessible PMR', labelEn: 'Wheelchair accessible', icon: 'i-lucide-accessibility' },
      { key: 'plain_pied', label: 'Plain-pied', labelEn: 'Single-storey', icon: 'i-lucide-home' },
    ],
  },
  confort: {
    label: 'Confort',
    labelEn: 'Comfort',
    icon: 'i-lucide-sparkles',
    items: [
      { key: 'wifi', label: 'Wifi gratuit', labelEn: 'Free Wi-Fi', icon: 'i-lucide-wifi' },
      { key: 'climatisation', label: 'Climatisation', labelEn: 'Air conditioning', icon: 'i-lucide-snowflake' },
      { key: 'piscine', label: 'Piscine', labelEn: 'Swimming pool', icon: 'i-lucide-waves' },
      { key: 'jardin', label: 'Jardin', labelEn: 'Garden', icon: 'i-lucide-trees' },
      { key: 'barbecue', label: 'Barbecue extérieur', labelEn: 'Outdoor barbecue', icon: 'i-lucide-flame' },
      { key: 'laverie', label: 'Laverie', labelEn: 'Laundry room', icon: 'i-lucide-washing-machine' },
      { key: 'cuisine_partagee_hotes', label: 'Cuisine partagée accessible aux hôtes', labelEn: 'Shared kitchen for guests', icon: 'i-lucide-utensils' },
    ],
  },
  cuisine_interieur: {
    label: 'Cuisine & intérieur',
    labelEn: 'Kitchen & interior',
    icon: 'i-lucide-cooking-pot',
    items: [
      { key: 'lave_vaisselle', label: 'Lave-vaisselle', labelEn: 'Dishwasher', icon: 'i-lucide-droplets', giteOnly: true },
      { key: 'lave_linge', label: 'Lave-linge', labelEn: 'Washing machine', icon: 'i-lucide-washing-machine', giteOnly: true },
      { key: 'four', label: 'Four', labelEn: 'Oven', icon: 'i-lucide-cooking-pot', giteOnly: true },
      { key: 'micro_ondes', label: 'Micro-ondes', labelEn: 'Microwave', icon: 'i-lucide-microwave', giteOnly: true },
      // `tv` : clé partagée avec le catalogue chambre — libellé et icône identiques.
      { key: 'tv', label: 'Télévision', labelEn: 'Television', icon: 'i-lucide-tv', giteOnly: true },
      { key: 'cheminee_poele', label: 'Cheminée / poêle', labelEn: 'Fireplace / wood stove', icon: 'i-lucide-flame', giteOnly: true },
    ],
  },
  bebe: {
    label: 'Bébé',
    labelEn: 'Baby',
    icon: 'i-lucide-baby',
    items: [
      { key: 'lit_bebe', label: 'Lit bébé', labelEn: 'Baby cot', icon: 'i-lucide-baby', giteOnly: true },
      // `chaise_haute` : clé partagée avec le catalogue chambre.
      { key: 'chaise_haute', label: 'Chaise haute', labelEn: 'High chair', icon: 'i-lucide-armchair', giteOnly: true },
    ],
  },
  bien_etre: {
    label: 'Bien-être',
    labelEn: 'Wellness',
    icon: 'i-lucide-sparkles',
    // Clés préfixées `bienetre_` : `spa_jacuzzi`, `sauna`, `hammam` et
    // `bain_nordique` sont déjà des prestations payantes (PAID_SERVICE_KEYS).
    items: [
      { key: 'bienetre_spa_jacuzzi', label: 'Spa / jacuzzi', labelEn: 'Spa / hot tub', icon: 'i-lucide-bath', giteOnly: true },
      { key: 'bienetre_sauna', label: 'Sauna', labelEn: 'Sauna', icon: 'i-lucide-thermometer-sun', giteOnly: true },
      { key: 'bienetre_hammam', label: 'Hammam', labelEn: 'Steam room', icon: 'i-lucide-cloud-fog', giteOnly: true },
      { key: 'bienetre_bain_nordique', label: 'Bain nordique', labelEn: 'Nordic bath', icon: 'i-lucide-snowflake', giteOnly: true },
    ],
  },
  exterieur: {
    label: 'Extérieur',
    labelEn: 'Outdoors',
    icon: 'i-lucide-sun',
    items: [
      { key: 'brasero', label: 'Brasero', labelEn: 'Fire pit', icon: 'i-lucide-flame-kindling' },
      { key: 'salon_jardin', label: 'Salon de jardin', labelEn: 'Garden furniture', icon: 'i-lucide-tree-palm', giteOnly: true },
      // `transats`, `terrasse_privee` : clés partagées avec le catalogue chambre.
      { key: 'transats', label: 'Transats', labelEn: 'Sun loungers', icon: 'i-lucide-recliner', giteOnly: true },
      { key: 'terrasse_privee', label: 'Terrasse privée', labelEn: 'Private terrace', icon: 'i-lucide-sun', giteOnly: true },
    ],
  },
  mobilite_douce: {
    label: 'Mobilité douce',
    labelEn: 'Soft mobility',
    icon: 'i-lucide-bike',
    items: [
      { key: 'borne_recharge_velo', label: 'Borne recharge vélo électrique', labelEn: 'E-bike charging point', icon: 'i-lucide-zap' },
      { key: 'station_reparation_velo', label: 'Station réparation vélo', labelEn: 'Bike repair station', icon: 'i-lucide-wrench' },
    ],
  },
  mobilite: {
    label: 'Mobilité',
    labelEn: 'Mobility',
    icon: 'i-lucide-plug-zap',
    items: [
      { key: 'borne_recharge_ve', label: 'Borne de recharge voiture électrique', labelEn: 'Electric car charging point', icon: 'i-lucide-plug-zap', giteOnly: true },
      { key: 'velos_disposition', label: 'Vélos à disposition', labelEn: 'Bikes available', icon: 'i-lucide-bike', giteOnly: true },
    ],
  },
  equestre: {
    label: 'Accueil équestre',
    labelEn: 'Equestrian facilities',
    icon: 'i-lucide-horse',
    items: [
      { key: 'box_chevaux', label: 'Box / paddock pour chevaux', labelEn: 'Horse stable / paddock', icon: 'i-lucide-fence' },
      { key: 'prairie_cloturee', label: 'Prairie clôturée', labelEn: 'Fenced meadow', icon: 'i-lucide-scan' },
      { key: 'acces_sentiers_equestres', label: 'Accès sentiers équestres', labelEn: 'Access to bridle paths', icon: 'i-lucide-map-pin' },
    ],
  },
  nature_outdoor: {
    label: 'Nature & outdoor',
    labelEn: 'Nature & outdoor',
    icon: 'i-lucide-trees',
    items: [
      { key: 'acces_foret_sentiers', label: 'Accès direct forêt / sentiers', labelEn: 'Direct access to forest / trails', icon: 'i-lucide-footprints' },
      { key: 'observation_astronomique', label: 'Observatoire / zone nuit noire', labelEn: 'Observatory / dark-sky area', icon: 'i-lucide-telescope' },
      { key: 'baignade_naturelle', label: 'Accès baignade naturelle', labelEn: 'Access to natural swimming', icon: 'i-lucide-waves' },
      { key: 'acces_ski', label: 'Accès ski (navette ou pistes)', labelEn: 'Ski access (shuttle or slopes)', icon: 'i-lucide-mountain-snow' },
    ],
  },
  terroir: {
    label: 'Agriculture & terroir',
    labelEn: 'Farming & local produce',
    icon: 'i-lucide-sprout',
    items: [
      { key: 'potager_cueillette', label: 'Potager / cueillette libre', labelEn: 'Vegetable garden / free picking', icon: 'i-lucide-carrot' },
      { key: 'ruche_miel', label: 'Ruche / production de miel', labelEn: 'Beehive / honey production', icon: 'i-lucide-flower-2' },
      { key: 'vigne_degustation', label: 'Vigne / dégustation sur place', labelEn: 'Vineyard / on-site tasting', icon: 'i-lucide-wine' },
      { key: 'ferme_pedagogique', label: 'Élevage / ferme pédagogique', labelEn: 'Livestock / educational farm', icon: 'i-lucide-egg' },
      { key: 'vente_produits_fermiers', label: 'Vente de produits fermiers', labelEn: 'Farm produce for sale', icon: 'i-lucide-shopping-basket' },
    ],
  },
  experiences: {
    label: 'Expériences & convivialité',
    labelEn: 'Experiences & get-togethers',
    icon: 'i-lucide-star',
    items: [
      { key: 'four_pain_pizza', label: 'Four à pain / à pizza', labelEn: 'Bread / pizza oven', icon: 'i-lucide-pizza' },
      { key: 'cave_vins', label: 'Cave à vins', labelEn: 'Wine cellar', icon: 'i-lucide-wine' },
      { key: 'billard', label: 'Billard', labelEn: 'Billiards', icon: 'i-lucide-circle-dot' },
      { key: 'baby_foot', label: 'Baby-foot', labelEn: 'Table football', icon: 'i-lucide-users' },
      { key: 'flechettes', label: 'Fléchettes', labelEn: 'Darts', icon: 'i-lucide-target' },
      { key: 'bibliotheque', label: 'Bibliothèque', labelEn: 'Library', icon: 'i-lucide-book-open' },
      { key: 'piano_instrument', label: 'Piano / instrument de musique', labelEn: 'Piano / musical instrument', icon: 'i-lucide-music' },
    ],
  },
  jeux_plein_air: {
    label: 'Jeux de plein air',
    labelEn: 'Outdoor games',
    icon: 'i-lucide-trophy',
    items: [
      { key: 'petanque', label: 'Pétanque', labelEn: 'Pétanque', icon: 'i-lucide-circle' },
      { key: 'molkky', label: 'Mölkky', labelEn: 'Mölkky', icon: 'i-lucide-log' },
      { key: 'badminton', label: 'Badminton', labelEn: 'Badminton', icon: 'i-lucide-feather' },
      { key: 'frisbee', label: 'Frisbee', labelEn: 'Frisbee', icon: 'i-lucide-disc' },
      { key: 'volleyball', label: 'Volley-ball', labelEn: 'Volleyball', icon: 'i-lucide-circle-dot' },
    ],
  },
} as const

/**
 * Type de bien pour lequel on saisit des équipements.
 * - `gite` : catalogue complet ;
 * - `establishment` (chambres d'hôtes) : sans les éléments `giteOnly`, déjà
 *   couverts côté chambres d'hôtes par les prestations payantes ou les
 *   équipements par chambre.
 */
export type EquipmentPropertyType = 'gite' | 'establishment'

/** Élément tel que déclaré dans le catalogue (marqueur réservé-gîte optionnel). */
interface CatalogItem {
  readonly key: string
  readonly label: string
  readonly labelEn: string
  readonly icon: string
  readonly giteOnly?: boolean
}

function isGiteOnly(item: CatalogItem): boolean {
  return item.giteOnly === true
}

/**
 * Clé petit-déjeuner — toujours inclus par définition chambre d'hôtes
 * Conservée dans le champ JSON equipments pour rétrocompatibilité lecture
 */
export const BREAKFAST_KEY = 'petit_dejeuner_inclus' as const

export const PETS_KEY = 'animaux_acceptes' as const

/**
 * Toutes les clés d'équipements valides (multi-select + spéciales)
 */
export const EQUIPMENT_KEYS = [
  ...Object.values(EQUIPMENT_CATEGORIES)
    .flatMap((category) => category.items.map((item) => item.key)),
  BREAKFAST_KEY,
  PETS_KEY,
]

/**
 * Clés acceptées pour un établissement chambres d'hôtes : catalogue complet
 * moins les éléments réservés au gîte (+ clés spéciales). Le gîte, lui,
 * valide contre `EQUIPMENT_KEYS`.
 */
export const ESTABLISHMENT_EQUIPMENT_KEYS = [
  ...Object.values(EQUIPMENT_CATEGORIES)
    .flatMap(category => (category.items as readonly CatalogItem[])
      .filter(item => !isGiteOnly(item))
      .map(item => item.key)),
  BREAKFAST_KEY,
  PETS_KEY,
]

/**
 * Type pour les clés d'équipements
 */
export type EquipmentKey = (typeof EQUIPMENT_KEYS)[number]

/**
 * Type pour une catégorie d'équipements
 */
export type EquipmentCategory = keyof typeof EQUIPMENT_CATEGORIES

/**
 * Type pour un item d'équipement
 */
export interface EquipmentItem {
  key: EquipmentKey
  label: string
  icon: string
}

/**
 * Langue d'affichage des libellés du catalogue (4.144.1). Les libellés EN
 * vivent à côté des FR, dans ce fichier et non dans i18n : le catalogue est
 * consommé par des fonctions pures (JSON-LD, tests) sans contexte Nuxt, et
 * garder les deux côte à côte rend un oubli détectable par un test exhaustif.
 */
export type EquipmentLocale = 'fr' | 'en'

/** Normalise un code de locale quelconque (`en`, `en-GB`, `fr`…) en `fr` | `en`. */
export function toEquipmentLocale(locale: string | null | undefined): EquipmentLocale {
  return typeof locale === 'string' && locale.toLowerCase().startsWith('en') ? 'en' : 'fr'
}

interface LookupEntry { label: string, labelEn: string, icon: string }

/**
 * Flat lookup map: equipment key → { label, labelEn, icon }
 * Includes category items + special keys (breakfast, pets)
 */
export const SPECIAL_EQUIPMENT_ENTRIES: ReadonlyArray<readonly [string, LookupEntry]> = [
  [BREAKFAST_KEY, { label: 'Petit-déjeuner inclus', labelEn: 'Breakfast included', icon: 'i-lucide-coffee' }],
  [PETS_KEY, { label: 'Animaux acceptés', labelEn: 'Pets allowed', icon: 'i-lucide-dog' }],
]

const EQUIPMENT_LOOKUP = new Map<string, LookupEntry>(
  [
    ...Object.values(EQUIPMENT_CATEGORIES).flatMap(cat =>
      (cat.items as readonly CatalogItem[]).map(item => [item.key, { label: item.label, labelEn: item.labelEn, icon: item.icon }] as const),
    ),
    ...SPECIAL_EQUIPMENT_ENTRIES,
  ],
)

/** Vrai si la clé appartient au catalogue (items + clés spéciales). */
export function isCatalogEquipmentKey(key: string): boolean {
  return EQUIPMENT_LOOKUP.has(key)
}

/**
 * Resolve an equipment key to its human-readable label (falls back to key).
 * `locale` vaut `fr` par défaut pour ne casser aucun appelant historique.
 */
export function getEquipmentLabel(key: string, locale: EquipmentLocale = 'fr'): string {
  const entry = EQUIPMENT_LOOKUP.get(key)
  if (!entry) return key
  return locale === 'en' ? entry.labelEn : entry.label
}

/** Resolve an equipment key to its icon (falls back to generic) */
export function getEquipmentIcon(key: string): string {
  return EQUIPMENT_LOOKUP.get(key)?.icon ?? 'i-lucide-check'
}

/** Catégorie du catalogue telle que proposée pour un type de bien donné. */
export interface EquipmentCategoryView {
  key: EquipmentCategory
  label: string
  icon: string
  items: { key: string, label: string, icon: string }[]
}

/**
 * Catégories proposées à la saisie pour un type de bien. Sans type : tout le
 * catalogue. Une catégorie dont tous les éléments sont réservés au gîte
 * disparaît pour un établissement chambres d'hôtes.
 */
export function getEquipmentCategoriesFor(propertyType?: EquipmentPropertyType): EquipmentCategoryView[] {
  return (Object.keys(EQUIPMENT_CATEGORIES) as EquipmentCategory[])
    .map((key) => {
      const category = EQUIPMENT_CATEGORIES[key]
      const items = (category.items as readonly CatalogItem[])
        .filter(item => propertyType !== 'establishment' || !isGiteOnly(item))
        .map(item => ({ key: item.key, label: item.label, icon: item.icon }))
      return { key, label: category.label, icon: category.icon, items }
    })
    .filter(category => category.items.length > 0)
}

/** Libellé du groupe qui recueille les clés hors catalogue (affichage public). */
export const OTHER_EQUIPMENT_GROUP_LABEL = 'Autres'
export const OTHER_EQUIPMENT_GROUP_LABEL_EN = 'Other'

export interface EquipmentGroup {
  /** Clé de catégorie, ou `autres` pour les clés hors catalogue. */
  key: EquipmentCategory | 'autres'
  label: string
  icon: string
  items: { key: string, label: string, icon: string }[]
}

const KEY_TO_CATEGORY = new Map<string, EquipmentCategory>(
  (Object.keys(EQUIPMENT_CATEGORIES) as EquipmentCategory[]).flatMap(cat =>
    (EQUIPMENT_CATEGORIES[cat].items as readonly CatalogItem[]).map(item => [item.key, cat] as const),
  ),
)

/**
 * Groupe des clés d'équipements par thème, pour l'affichage public.
 *
 * - groupes et éléments dans l'ordre du catalogue ;
 * - thèmes vides absents ;
 * - clés hors catalogue (clé chambre sur un gîte, clés spéciales…) réunies
 *   dans un dernier groupe « Autres », dans leur ordre d'apparition.
 *
 * `resolve` laisse l'appelant choisir ses résolveurs de libellé/icône : la
 * fiche gîte passe par la cascade `getGiteEquipmentLabel/Icon`. Omis (ou
 * `undefined`), on résout dans le catalogue selon `locale`. `locale` traduit
 * aussi les libellés de thèmes et du groupe « Autres ».
 */
export function groupEquipmentsByTheme(
  keys: readonly string[],
  resolve?: { label: (key: string) => string, icon: (key: string) => string },
  locale: EquipmentLocale = 'fr',
): EquipmentGroup[] {
  const resolver = resolve ?? { label: (key: string) => getEquipmentLabel(key, locale), icon: getEquipmentIcon }
  const present = new Set(keys)
  const groups: EquipmentGroup[] = []

  for (const cat of Object.keys(EQUIPMENT_CATEGORIES) as EquipmentCategory[]) {
    const category = EQUIPMENT_CATEGORIES[cat]
    const items = (category.items as readonly CatalogItem[])
      .filter(item => present.has(item.key))
      .map(item => ({ key: item.key, label: resolver.label(item.key), icon: resolver.icon(item.key) }))
    if (items.length > 0) {
      groups.push({ key: cat, label: locale === 'en' ? category.labelEn : category.label, icon: category.icon, items })
    }
  }

  const others = [...present]
    .filter(key => !KEY_TO_CATEGORY.has(key))
    .map(key => ({ key, label: resolver.label(key), icon: resolver.icon(key) }))
  if (others.length > 0) {
    const label = locale === 'en' ? OTHER_EQUIPMENT_GROUP_LABEL_EN : OTHER_EQUIPMENT_GROUP_LABEL
    groups.push({ key: 'autres', label, icon: 'i-lucide-list', items: others })
  }

  return groups
}
